import { useEffect, useRef, useMemo, useState } from 'react';
import { generatePath, useLocation, NavLink, Outlet } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  IconExpand,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  Filters,
  SelectItemProps,
  IconSelected,
  IconArrowRight,
  useGetList,
  ButtonSize,
  ClickOutsideDetector,
  IconUp,
  IconDown,
  usePrevious,
  Popup,
  ButtonPseudo,
  PermissionEnum,
  SchemaFilterType,
  formatSearchParams,
  formatDate,
  InputText,
  IconSearch,
  formatPhone,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setHintVisible,
  selectList,
  clearIndividualList,
} from './store/slices/list';
import {
  CustomerProps,
  CustomerStatus,
  OrganizationType,
} from '../customers/types';
import { useLazyGetIndividualUsersQuery } from './store/services/individual';
import { useGetNewApplicationsValidatorsQuery } from '../new-application/store/services';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../store/dictionaries/services';
import {
  customersListColumns,
  customersListSchema,
  customersListFiltersSchema,
  customersListColumnsLabels,
} from './variables/list';
import { clearGeneralState } from './store/slices/general-info';
import { clearOverviewState } from './store/slices/overview/overview';
import { clearDocumentsState } from '../customers/store/slices/documents/documents';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../customers/store/slices/statements';
import { clearAccountsState } from '../customers/store/slices/accounts-statements';
import { clearStatisticState } from '../customers/store/slices/statistic';
import { clearAmlState } from './store/slices/aml/aml';
import { clearTariffsState } from '../customers/store/slices/tariffs/tariff';
import { clearDevicesState } from './store/slices/devices';
import { clearExternalAccounts } from '../customers/store/slices/external-accounts';
import {
  clearLocksState,
  clearLogsState,
  setOrganizationId,
} from './store/slices/security';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const formatCustomersList = (
  list: CustomerProps[],
  countries?: SelectItemProps[],
): CustomerProps[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let status;

    if (result.status === CustomerStatus.ACTIVE) {
      status = 'Active';
    } else if (result.status === CustomerStatus.CLOSED) {
      status = 'Closed';
    } else if (result.status === CustomerStatus.SUSPENDED) {
      status = 'Suspended';
    } else {
      status = 'Disabled';
    }

    const render: Record<string, React.JSX.Element> = {
      type: (
        <div>
          {result.type === OrganizationType.INDIVIDUAL
            ? 'Individual'
            : 'Company Official'}
        </div>
      ),
      ids: <div className={cn.organizationIdWrapper}>{result?.ids}</div>,
      dob: <div>{result?.dob ? formatDate(result?.dob) : '-'}</div>,
      countryId: (
        <div>
          {
            countries?.find((country) => country.value === result.countryId)
              ?.name
          }
        </div>
      ),
      status: (
        <div
          className={`${
            result.status === CustomerStatus.ACTIVE ? cn.activeStatus : ''
          } ${result.status === CustomerStatus.CLOSED ? cn.closedStatus : ''}`}
        >
          {status}
        </div>
      ),
      phone: <div>{formatPhone(result?.user?.phone)}</div>,
      email: (
        <div className={cn.organizationEmailWrapper}>
          {result?.user?.profile?.additionalEmail || result?.user?.email}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const IndividualList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    customersDictionaries: { dictionariesIsLoading, applicationSchemes },
    individualCustomers: {
      list: {
        individualCustomers,
        requestInProgress,
        hintVisible,
        createdApplicationId,
        pagination,
        selectedLists,
      },
      info: {
        general: { baseInformation },
        overview: { generalUserInfo, baseUserInformation },
      },
    },
  } = useAppSelector((state) => state);
  const { countriesList: countries, activePermissions } = useAppSelector(
    (state) => state.root,
  );
  const [countriesList, setCountriesList] = useState<
    SelectItemProps[] | undefined
  >(undefined);

  const [nameSearch, setNameSearch] = useState('');

  useGetNewApplicationsValidatorsQuery();

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const { filters, onFilterChange } = useQueryState(customersListSchema);

  const prevFilters = usePrevious(filters);
  const prevPagination = usePrevious(pagination);

  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();

  const [getIndividualUserList, { isFetching, isLoading }] =
    useLazyGetIndividualUsersQuery();
  const [updateSelectedListVisible, setUpdateSelectedListVisible] =
    useState(false);
  const [updateAllListVisible, setUpdateAllListVisible] = useState(false);
  const [action, setAction] = useState('');
  const [popupVisible, setPopupVisible] = useState(false);

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<CustomerProps>({
      getList: getIndividualUserList,
      onFilterChange,
      onSortChange: () => {},
      schema: customersListSchema,
    });

  const formattedData = useMemo(() => {
    const data = formatCustomersList(
      individualCustomers as CustomerProps[],
      countries,
    );

    return data;
  }, [individualCustomers]);

  // const onRowsSelect = (rows: string[]) => {
  //   if (!isEqual(selectedLists, rows)) {
  //     dispatch(selectList(rows));
  //   }
  // };

  const onSearchAutocomplete = (name: string, value: string) => {
    const nextCountries = countries?.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase()),
    );

    setCountriesList(nextCountries);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: customersListFiltersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useScroll({
    element: scrollRef,
    requestInProgress,
  });

  const onClear = () => {
    dispatch(clearOverviewState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearAccountsState());
    dispatch(clearStatisticState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearAmlState());
    dispatch(clearExternalAccounts());
    dispatch(clearDevicesState());
    dispatch(clearLocksState());
    dispatch(clearLogsState());
    dispatch(setOrganizationId(''));
  };

  useEffect(() => {
    if (baseInformation) {
      dispatch(clearGeneralState());
    }

    if (generalUserInfo || baseUserInformation) {
      onClear();
    }
  }, [generalUserInfo, baseInformation, baseUserInformation]);

  useEffect(() => {
    dispatch(clearDocumentsState());
  }, []);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [applicationSchemes, activePermissions]);

  useEffect(() => {
    if (!countriesList && countries) {
      setCountriesList(countries);
    }
  }, [countries, countriesList]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !individualCustomers
    ) {
      let currentFilters = { ...filters };
      const currentFiltersStorage = localStorage.getItem(
        'individualOrganizationsFilters',
      );

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            currentFilters.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.type) {
          currentFilters.type = {
            value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
            type: SchemaFilterType.STRING,
          };
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilterChange(currentFilters);
      }

      if (!filters.type && !currentFiltersStorage) {
        const filtersValues: Filters = {
          ...currentFilters,
        };

        filtersValues.type = {
          value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
          type: SchemaFilterType.STRING,
        };

        if (!filters.status) {
          filtersValues.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            filtersValues.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        onFilter({
          ...filtersValues,
        });
      } else if (!individualCustomers) {
        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            currentFilters.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    }
  }, [filters, prevFilters, individualCustomers, search]);

  useEffect(() => {
    if (!individualCustomers && search) {
      formatSearchParams({
        currentSchemaValues: customersListSchema,
        storageKey: 'individualOrganizationsFilters',
        search,
      });
    }
  }, [individualCustomers, search]);

  useEffect(() => {
    const currentFilters = localStorage.getItem(
      'individualOrganizationsFilters',
    );

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'individualOrganizationsFilters',
          JSON.stringify({
            ...parsed,
          }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    if (countriesList) {
      setValues('countryId', countriesList);
    }
  }, [countriesList]);

  useEffect(() => {
    dispatch(clearIndividualList());
  }, []);

  let content;
  let popupTitle;
  let popupButtonText = 'Yes, Apply';

  if (action === 'updateSelected') {
    popupTitle = `Calculated risk score will be updated for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;

    popupButtonText = 'Yes, update';
  } else if (action === 'applySelected') {
    popupTitle = `Current calculated risk score will be applied for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;
  } else if (action === 'updateAndApplySelected') {
    popupTitle = `Calculated Risk Score will be updated and applied for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;
  } else if (action === 'updateAll') {
    popupTitle = 'Calculated risk score will be updated for all clients';
    popupButtonText = 'Yes, update';
  } else if (action === 'applyAll') {
    popupTitle =
      'Current calculated risk score will be applied for all clients';
  } else if (action === 'updateAndApplyAll') {
    popupTitle =
      'Calculated Risk Score will be updated and applied for all clients';
  }

  if (isLoading || dictionariesIsLoading) {
    content = (
      <div className={cn.customersLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && countriesList) {
    content = (
      <Table
        columns={customersListColumns}
        data={formattedData || []}
        sort={{}}
        onSort={() => {}}
        filters={filters}
        error={errorList}
        columnsLabels={customersListColumnsLabels}
        tableFormClassName={cn.customersListForm}
        onFilterChange={(values) => {
          const nextValues = {
            ...values,
          };

          if (nameSearch) {
            nextValues.search = {
              value: [nameSearch],
              type: SchemaFilterType.STRING,
            };
          }

          if (values.type) {
            if (!nextValues.status) {
              nextValues.status = {
                value: ['ACTIVE', 'CLOSED'],
                type: SchemaFilterType.STRING,
              };
            }

            onFilter(nextValues);
          } else {
            const nextFilters: Filters = {
              type: {
                value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
                type: SchemaFilterType.STRING,
              },
              status: {
                value: ['ACTIVE', 'CLOSED'],
                type: SchemaFilterType.STRING,
              },
            };

            if (nameSearch) {
              nextFilters.search = {
                value: [search],
                type: SchemaFilterType.STRING,
              };
            }

            onFilter({ ...nextFilters });
          }

          localStorage.setItem(
            'individualOrganizationsFilters',
            JSON.stringify({
              ...nextValues,
            }),
          );
        }}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        hiddenFiltersTags={
          filters?.status?.value.length === 2 ? ['status'] : []
        }
        take="20"
        setPage={(data) => {
          const nextFilters: Filters = {
            ...filters,
          };

          if (nameSearch) {
            nextFilters.search = {
              value: [search],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: nextFilters });

          localStorage.setItem(
            'individualOrganizationsFilters',
            JSON.stringify({
              ...filters,
              cursor: {
                value: [data.id],
                type: SchemaFilterType.STRING,
              },
            }),
          );
        }}
        storeKey="individual-organizations"
        pagination={pagination}
        withSettings
        clickable
      />
    );
  }

  return (
    <div className={cn.customersBottom} ref={scrollRef}>
      <div className={cn.customersTopContent}>
        <div className={cn.customerTopContentTitle}>Individual Customers</div>
        <div className={cn.clientsTop}>
          <NavLink
            to={`${ROUTES.CUSTOMERS_INDIVIDUAL_CREATE}${search || ''}`}
            className={cn.customersCreateLink}
          >
            <div className={cn.customersCreateLinkContent}>
              <IconExpand className={cn.customersCreateLinkIcon} />
              <span>Create new customer</span>
            </div>
          </NavLink>

          {selectedLists.length > 0 &&
            activePermissions?.includes(
              PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_RISK_ACTION,
            ) && (
              <>
                <div className={cn.clientsTopSeparator} />
                <div
                  className={`${cn.clientsUpdateWrapper} ${
                    updateSelectedListVisible
                      ? cn.clientsUpdateWrapperActive
                      : ''
                  }`}
                >
                  <ClickOutsideDetector
                    setOptionsListVisible={setUpdateSelectedListVisible}
                    className={cn.clientsUpdateContent}
                  >
                    <>
                      <ButtonPseudo
                        className={`button-white ${cn.updateClientsButton}`}
                        size={ButtonSize.SMALL}
                        text={`Update risk score for ${selectedLists?.length} customer${
                          selectedLists?.length > 1 ? 's' : ''
                        }`}
                        onClick={() =>
                          setUpdateSelectedListVisible(
                            !updateSelectedListVisible,
                          )
                        }
                      />
                      {updateSelectedListVisible ? <IconUp /> : <IconDown />}
                      {updateSelectedListVisible && (
                        <ul className={cn.updateOptionList}>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('updateSelected');
                              }}
                            >
                              Update calculated risk score
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('applySelected');
                              }}
                            >
                              Apply calculated risk score
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('updateAndApplySelected');
                              }}
                            >
                              Update and apply calculated risk score
                            </button>
                          </li>
                        </ul>
                      )}
                    </>
                  </ClickOutsideDetector>
                </div>
                <div
                  className={`${cn.clientsUpdateWrapper} ${
                    updateAllListVisible ? cn.clientsUpdateWrapperActive : ''
                  }`}
                >
                  <ClickOutsideDetector
                    setOptionsListVisible={setUpdateAllListVisible}
                    className={cn.clientsUpdateContent}
                  >
                    <>
                      <ButtonPseudo
                        className={`button-white ${cn.updateClientsButton}`}
                        size={ButtonSize.SMALL}
                        text="Update risk score for all customers"
                        onClick={() =>
                          setUpdateAllListVisible(!updateAllListVisible)
                        }
                      />
                      {updateAllListVisible ? <IconUp /> : <IconDown />}
                      {updateAllListVisible && (
                        <ul className={cn.updateOptionList}>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('updateAll');
                              }}
                            >
                              Update calculated risk score
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('applyAll');
                              }}
                            >
                              Apply calculated risk score
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setPopupVisible(true);
                                setAction('updateAndApplyAll');
                              }}
                            >
                              Update and apply calculated risk score
                            </button>
                          </li>
                        </ul>
                      )}
                    </>
                  </ClickOutsideDetector>
                </div>
              </>
            )}
        </div>
        <InputText
          name="nameSearch"
          labelClassName={cn.nameSearch}
          value={nameSearch}
          placeholder="Enter name"
          prefix={<IconSearch />}
          prefixClassName={cn.nameSearchIcon}
          onChange={(e) => {
            clearTimeout(timeout);

            setNameSearch(e.target.value);

            const nextFilters: Filters = {
              ...filters,
            };

            if (e.target.value) {
              nextFilters.search = {
                value: [e.target.value],
                type: SchemaFilterType.STRING,
              };
            } else if (!e.target.value && nextFilters.search) {
              delete nextFilters.search;
            }

            timeout = setTimeout(() => {
              onFilter({
                ...nextFilters,
              });
            }, 500);
          }}
        />
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={cn.applicationCreatedSuccessful}>
          <div className={cn.applicationCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={cn.applicationCreatedSuccessfulRight}>
            <span>
              <NavLink
                to={generatePath(
                  ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO,
                  {
                    applicationId: createdApplicationId as string,
                  },
                )}
              >
                <IconArrowRight />
                Open application
              </NavLink>
            </span>
            <button
              type="button"
              className={cn.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Popup
        title={popupTitle}
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => setPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText={popupButtonText}
        isVisible={popupVisible}
      />
    </div>
  );
};

export default IndividualList;
